// Rename the device behind a unique number — inline, for the Message Box
// (both shells), beside DeviceTypePicker.
//
// Goes through api.renameDeviceInstalled, which patches the phone cache and
// flags every other user's phone (core/db/markSinkData) — never a raw post.
//
// ⚠️ Alexa resolves a spoken name to ONE thing, account-wide, so a NEW name is
// checked against the OWNER's index (DevicesInstalled.MasterUserId) — the same
// rule as the phone's rename (core/devices/alexaNames). Only a changed name is
// checked: the factory defaults already collide all over the estate.

import React, { useEffect, useState } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress,
} from '@mui/material';
import { renameDeviceInstalled } from '../../core/api/api';
import { loadNameIndex } from '../../core/devices/nameIndex';
import {
  findClash, clashMessage, normaliseName, deviceKeyOf,
} from '../../core/devices/alexaNames';

export default function DeviceNameField({ device, onSaved, dense = false }) {
  const uno = device && device.UniqueNumber;
  const current = String((device && device.DeviceName) || '');
  const [name, setName] = useState(current);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);   // { ok, text }

  // A different device is a different answer: never carry an unsaved name over.
  useEffect(() => { setName(current); setNote(null); }, [uno, current]);

  const trimmed = name.trim();
  const changed = !!trimmed && trimmed !== current.trim();

  const save = async () => {
    if (!changed || busy) return;
    setBusy(true);
    setNote(null);
    try {
      if (normaliseName(trimmed) !== normaliseName(current)) {
        const owner = Number(device.MasterUserId) || null;
        const index = owner ? await loadNameIndex(owner).catch(() => null) : null;
        const clash = index && findClash(index, trimmed, deviceKeyOf(uno));
        if (clash) { setNote({ ok: false, text: clashMessage(clash) }); return; }
      }
      await renameDeviceInstalled(uno, trimmed);
      setNote({ ok: true, text: 'Name saved.' });
      if (onSaved) onSaved();
    } catch (e) {
      setNote({ ok: false, text: 'The name could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexDirection: dense ? 'column' : 'row' }}>
        <TextField
          size="small" fullWidth label="Device name"
          value={name}
          onChange={(e) => { setName(e.target.value); setNote(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          disabled={!uno || busy}
          inputProps={{ style: { fontSize: 12.5 } }}
        />
        <Button
          size="small" variant="contained" onClick={save} disabled={!changed || busy}
          sx={{ textTransform: 'none', minWidth: 72, alignSelf: dense ? 'stretch' : 'auto' }}
        >
          {busy ? <CircularProgress size={16} /> : 'Rename'}
        </Button>
      </Box>
      {note && (
        <Typography sx={{ fontSize: 11.5, mt: 0.25, color: note.ok ? 'success.main' : 'error.main' }}>
          {note.text}
        </Typography>
      )}
    </Box>
  );
}
